import React, { useState } from 'react';
import { Plus, Trash2, LayoutGrid } from 'lucide-react';
import { Card, Button, Input } from '../../components/ui/index.jsx';
import { useGet } from '../../hooks/useApi';
import api from '../../utils/api';
import { useAuth } from '../../contexts/AuthContext';
import { useToast } from '../../contexts/ToastContext';


const InstitutionList = () => {
    const [currentPage, setCurrentPage] = useState(1);
    const { data: institutionsResponse, refresh, loading } = useGet(`/institutions?page=${currentPage}`);
    
    // Resilient data extraction
    const institutions = Array.isArray(institutionsResponse) ? institutionsResponse : (institutionsResponse?.data || []);
    const pagination = institutionsResponse?.meta || null;
    const { hasPermission } = useAuth();
    const canCreate = hasPermission('institutions', 'create');
    const canDelete = hasPermission('institutions', 'delete');
    const { success, error: toastError } = useToast();


    const [name, setName] = useState('');
    const [isSubmitting, setIsSubmitting] = useState(false);

    const handleAdd = async (e) => {
        e.preventDefault();
        setIsSubmitting(true);
        try {
            await api.post('/institutions', { name });
            success('تم إضافة المؤسسة بنجاح');
            setName(''); 
            refresh();
        } catch (err) { 
            toastError('خطأ أثناء الإضافة'); 
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleDelete = async (id) => {
        if(confirm('هل أنت متأكد من حذف هذه المؤسسة؟')) {
            try {
                await api.delete(`/institutions/${id}`);
                success('تم حذف المؤسسة بنجاح');
                refresh();
            } catch (err) {
                toastError(err?.response?.data?.message || 'خطأ أثناء الحذف');
            }
        }
    };

    return (
        <div className="space-y-8 animate-in fade-in duration-500">
            <div className="flex flex-col md:flex-row md:items-end justify-between gap-4">
                <div>
                    <h2 className="text-xl text-slate-800">إدارة المؤسسات</h2>
                    <p className="text-slate-400 text-[11px] leading-none mt-1">تنظيم وتصنيف المؤسسات التعليمية للتلاميذ</p>
                </div>
                <div className="flex bg-white px-4 py-2 rounded-lg border border-slate-100 items-center gap-2">
                    <LayoutGrid className="text-primary w-5 h-5" />
                    <span className="text-sm text-slate-600">إجمالي المؤسسات: {pagination?.total || institutions.length || 0}</span>
                </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                {/* Form Section */}
                {canCreate ? (
                    <Card className="p-4 h-fit lg:col-span-1 border-primary/10 bg-orange-50/10">
                        <h3 className="text-lg text-slate-800 mb-6 flex items-center gap-2">
                            <Plus className="w-5 h-5 text-primary" /> إضافة مؤسسة جديدة
                        </h3>
                        <form onSubmit={handleAdd} className="space-y-4">
                            <Input 
                                label="اسم المؤسسة" 
                                value={name} 
                                onChange={e => setName(e.target.value)} 
                                placeholder="مثال: مدرسة الأمل" 
                                required 
                            />
                            <Button type="submit" isLoading={isSubmitting} className="w-full mt-2">
                                حفظ المؤسسة
                            </Button>
                        </form>
                    </Card>
                ) : (
                    <Card className="p-8 h-fit lg:col-span-1 border-slate-100 bg-slate-50/50 text-center flex flex-col items-center justify-center">
                        <LayoutGrid className="w-8 h-8 text-slate-300 mb-2" />
                        <p className="text-xs text-slate-400">لا تملك صلاحية إضافة مؤسسات جديدة</p>
                    </Card>
                )}

                {/* List Section */}
                <Card className="lg:col-span-2 overflow-hidden border-slate-100">
                    <div className="overflow-x-auto">
                        <table className="w-full text-right">
                            <thead className="bg-slate-50 border-b border-slate-100">
                                <tr>
                                    <th className="px-6 py-2.5 text-[10px] text-slate-400 uppercase tracking-widest">المعرف</th>
                                    <th className="px-6 py-2.5 text-[10px] text-slate-400 uppercase tracking-widest">اسم المؤسسة</th>
                                    <th className="px-6 py-2.5 text-[10px] text-slate-400 uppercase tracking-widest text-left">الإجراءات</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-slate-100">
                                {loading && (
                                    <tr>
                                        <td colSpan="3" className="px-6 py-8 text-center text-slate-400 animate-pulse">جاري التحميل...</td>
                                    </tr>
                                )}
                                {!loading && institutions?.length === 0 && (
                                    <tr>
                                        <td colSpan="3" className="px-6 py-8 text-center text-slate-400">لا توجد مؤسسات مسجلة حالياً</td>
                                    </tr>
                                )}
                                {institutions?.map((item) => (
                                    <tr key={item.id} className="hover:bg-slate-50/50 transition-colors">
                                        <td className="px-6 py-4">
                                            <span className="text-primary bg-primary/5 px-2 py-1 rounded text-sm">
                                                #{item.id}
                                            </span>
                                        </td>
                                        <td className="px-6 py-4 text-slate-800">{item.name}</td>
                                        <td className="px-6 py-4 text-left">
                                            {canDelete ? (
                                                <button 
                                                    onClick={() => handleDelete(item.id)} 
                                                    className="p-2 text-slate-400 hover:text-red-500 hover:bg-red-50 rounded-lg transition-all"
                                                    title="حذف المؤسسة"
                                                >
                                                    <Trash2 size={16}/>
                                                </button>
                                            ) : (
                                                <span className="text-[10px] text-slate-300">غير مسموح</span>
                                            )}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>

                    {/* Pagination Controls */}
                    {pagination && pagination.last_page > 1 && (
                        <div className="px-6 py-4 bg-slate-50/30 border-t border-slate-100 flex items-center justify-between">
                            <div className="text-xs text-slate-500 font-normal">
                                عرض <span className="font-medium text-slate-700">{pagination.from}</span> إلى <span className="font-medium text-slate-700">{pagination.to}</span> من أصل <span className="font-medium text-slate-700">{pagination.total}</span> مؤسسة
                            </div>
                            <div className="flex items-center gap-2">
                                <Button 
                                    variant="outline" 
                                    size="sm" 
                                    onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                                    disabled={currentPage === 1}
                                    className="h-8 px-3 text-xs"
                                >
                                    السابق
                                </Button>
                                <div className="flex items-center gap-1">
                                    {Array.from({ length: Math.min(5, pagination.last_page) }, (_, i) => {
                                        let pageNum = i + 1;
                                        if (pagination.last_page > 5 && currentPage > 3) {
                                            pageNum = currentPage - 3 + i + 1;
                                            if (pageNum > pagination.last_page) pageNum = pagination.last_page - (4 - i);
                                        }
                                        return (
                                            <button
                                                key={pageNum}
                                                onClick={() => setCurrentPage(pageNum)}
                                                className={`w-8 h-8 rounded-lg text-xs transition-all ${
                                                    currentPage === pageNum 
                                                    ? 'bg-primary text-white shadow-sm font-medium' 
                                                    : 'text-slate-500 hover:bg-slate-100'
                                                }`}
                                            >
                                                {pageNum}
                                            </button>
                                        );
                                    })}
                                </div>
                                <Button 
                                    variant="outline" 
                                    size="sm" 
                                    onClick={() => setCurrentPage(prev => Math.min(prev + 1, pagination.last_page))}
                                    disabled={currentPage === pagination.last_page}
                                    className="h-8 px-3 text-xs"
                                >
                                    التالي
                                </Button>
                            </div>
                        </div>
                    )}
                </Card>
            </div>
        </div>
    );
};

export default InstitutionList;
